export type AnalyticsPeriod = [string, string];
export type PeriodPreset = "this-week" | "last-week" | "this-month" | "last-month" | "7" | "28" | "30" | "90";
const DAY = 86_400_000;

export function calendarDate(value: string | null): value is string {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const date = new Date(`${value}T00:00:00Z`);
  return Number.isFinite(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
function addDays(date: string, days: number) {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY).toISOString().slice(0, 10);
}
export function reportingToday(timezone = "UTC", now = new Date()): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: timezone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  return ["year", "month", "day"].map(type => parts.find(part => part.type === type)!.value).join("-");
}
export function periodPreset(preset: PeriodPreset, today = reportingToday()): AnalyticsPeriod {
  if (preset === "this-week" || preset === "last-week") {
    const monday = addDays(today, -((new Date(`${today}T00:00:00Z`).getUTCDay() + 6) % 7));
    return preset === "this-week" ? [monday, today] : [addDays(monday, -7), addDays(monday, -1)];
  }
  const monthStart = `${today.slice(0, 7)}-01`;
  if (preset === "this-month") return [monthStart, today];
  if (preset === "last-month") { const end = addDays(monthStart, -1); return [`${end.slice(0, 7)}-01`, end]; }
  return [addDays(today, 1 - Number(preset)), today];
}
export function readPeriod(search: URLSearchParams, today = reportingToday()): AnalyticsPeriod {
  const start = search.get("start_date"), end = search.get("end_date");
  return calendarDate(start) && calendarDate(end) && start <= end && end <= today ? [start, end] : periodPreset("28", today);
}
export function shiftPeriod(period: AnalyticsPeriod, direction: -1 | 1, preset?: string | null, today?: string): AnalyticsPeriod {
  if (preset === "this-month" || preset === "last-month") {
    const date = new Date(`${period[0].slice(0, 7)}-01T00:00:00Z`);
    date.setUTCMonth(date.getUTCMonth() + direction);
    const start = date.toISOString().slice(0, 10);
    date.setUTCMonth(date.getUTCMonth() + 1);
    const end = addDays(date.toISOString().slice(0, 10), -1);
    return [start, today && start <= today && end > today ? today : end];
  }
  const length = Math.round((Date.parse(period[1]) - Date.parse(period[0])) / DAY) + 1;
  return [addDays(period[0], direction * length), addDays(period[1], direction * length)];
}
export function monthDates(month: string): string[] {
  const start = `${month}-01`;
  const offset = (new Date(`${start}T00:00:00Z`).getUTCDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, index) => addDays(start, index - offset));
}
export function previousPeriod(period: AnalyticsPeriod, preset?: string | null): AnalyticsPeriod {
  if (preset === "this-week") return [addDays(period[0], -7), addDays(period[1], -7)];
  if (preset === "this-month" || preset === "last-month") {
    const end = addDays(`${period[0].slice(0, 7)}-01`, -1);
    const start = `${end.slice(0, 7)}-01`;
    return preset === "last-month" ? [start, end] : [start, addDays(start, Math.min(Number(period[1].slice(-2)), Number(end.slice(-2))) - 1)];
  }
  return shiftPeriod(period, -1);
}
export function scopedAnalyticsHref(path: string, search: URLSearchParams, period: AnalyticsPeriod, patch: Record<string, string | null> = {}): string {
  const next = new URLSearchParams(search);
  for (const [key, value] of Object.entries({ start_date: period[0], end_date: period[1], ...patch })) {
    if (value === null) next.delete(key); else next.set(key, value);
  }
  // Pagination belongs to one exact result set; detail navigation keeps its parent page.
  const resultKeys = ["start_date", "end_date", "period", "compare", "surface", "source_assignment", "report", "view", "page", "search", "sort", "direction", "attention", "ctr_limit", "impressions_min", "change_status", "change_metric"];
  if (resultKeys.some(key => next.get(key) !== search.get(key))) next.delete("report_page");
  return `${path}?${next}`;
}

const CONTEXT_KEYS = ["start_date", "end_date", "period", "compare"] as const;
function contextKey(brandId: string, assignmentId: string) { return `analytics-period:${encodeURIComponent(brandId)}:${encodeURIComponent(assignmentId)}`; }
export function rememberConnectionPeriod(brandId: string, assignmentId: string, search: URLSearchParams, period: AnalyticsPeriod): void {
  try {
    const saved = new URLSearchParams({ start_date: period[0], end_date: period[1] });
    for (const key of ["period", "compare"]) if (search.has(key)) saved.set(key, search.get(key)!);
    window.sessionStorage.setItem(contextKey(brandId, assignmentId), saved.toString());
  } catch { /* Storage can be unavailable in private browser sessions. URL remains authoritative. */ }
}
export function connectionPeriodSearch(brandId: string, assignmentId: string, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current);
  try {
    const raw = window.sessionStorage.getItem(contextKey(brandId, assignmentId));
    if (!raw) return next;
    const saved = new URLSearchParams(raw);
    if (!calendarDate(saved.get("start_date")) || !calendarDate(saved.get("end_date")) || saved.get("start_date")! > saved.get("end_date")!) return next;
    for (const key of CONTEXT_KEYS) { const value = saved.get(key); if (value) next.set(key, value); else next.delete(key); }
  } catch { /* Scope navigation works without storage. */ }
  return next;
}
